import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Visitor counter strip — small, friendly, retro-flavoured.
 *
 * Strategy
 * --------
 *  • Fires POST /metrics/visit on first mount per browser session
 *    (sessionStorage flag — survives soft navs, resets on tab close).
 *    Dedup is enforced server-side too via cc_visit_sid cookie + a
 *    unique (sid, day) index, so refresh-spammers can't game it.
 *  • Renders a single horizontal pill with the count + an animated
 *    odometer-style roll on increment.
 *  • Failsafe: hides itself entirely on error so a flaky metrics
 *    endpoint can never block the page.
 */
export default function VisitorCounter({ className = "", testId = "visitor-counter" }) {
  const [visits, setVisits] = useState(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Only POST once per browser session — soft navigation between routes
        // shouldn't double-count. sessionStorage access can throw in private
        // mode / sandboxed iframes, so guard it.
        let alreadyCounted = false;
        try {
          alreadyCounted = sessionStorage.getItem("cc_counted") === "1";
        } catch { /* storage unavailable — treat as fresh visit */ }
        const resp = alreadyCounted
          ? await api.get("/metrics/visit")
          : await api.post("/metrics/visit");
        if (cancelled) return;
        const n = Number(resp?.data?.visits || 0);
        if (n > 0) {
          setVisits(n);
          if (!alreadyCounted) {
            try { sessionStorage.setItem("cc_counted", "1"); } catch { /* ignore */ }
            // tiny pop animation on the very first paint
            setTimeout(() => !cancelled && setAnimated(true), 80);
          }
        }
      } catch {
        // Silently fail — counter just won't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (visits == null) return null;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 border hairline-strong bg-white/95 backdrop-blur-sm ${className}`}
      data-testid={testId}
      aria-label={`${visits.toLocaleString()} camper visits to date`}
    >
      <Users className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={2} />
      <span className="text-[10px] font-bold uppercase tracking-[1.5px] text-[#888]">
        Visits
      </span>
      <span
        className={`mono text-sm font-bold text-[#111] tabular-nums transition-transform duration-500 ${animated ? "scale-110" : "scale-100"}`}
        data-testid="visitor-counter-number"
      >
        {visits.toLocaleString()}
      </span>
    </div>
  );
}
